import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PaginationInstance } from 'ngx-pagination';
import { forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ProductService } from 'src/app/services/product.service';

@Component({
  standalone: false,
  selector: 'app-products-list',
  templateUrl: './products-list.component.html',
})
export class ProductsListComponent implements OnInit {
  items: any[] = [];
  syncing = false;
  paginationConfig: PaginationInstance = {
    id: 'products_listing',
    itemsPerPage: 10,
    totalItems: 0,
    currentPage: 1,
  };

  constructor(
    private productService: ProductService,
    private toaster: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadItems();
  }

  syncProducts(): void {
    if (this.syncing) {
      return;
    }
    this.syncing = true;
    this.productService.sync({}).subscribe({
      next: (result) => {
        this.toaster.success(result?.message || 'Products synced successfully.');
        // Job may finish after the HTTP response; reload shortly so new rows appear.
        setTimeout(() => {
          this.loadItems();
          this.syncing = false;
          this.cdr.detectChanges();
        }, 1500);
      },
      error: (error) => {
        this.syncing = false;
        const body = error?.error;
        this.toaster.error(
          body?.error || body?.message || 'Product sync failed.',
        );
        this.cdr.detectChanges();
      },
    });
  }

  loadItems(): void {
    const perPage = 100;
    this.productService
      .list({ page: 1, per_page: perPage })
      .pipe(
        switchMap((result) => {
          const data = result?.data ?? {};
          const firstPage = data.products ?? [];
          const lastPage = Math.max(1, data.last_page ?? 1);
          if (lastPage <= 1) {
            return of(firstPage);
          }
          const requests = [];
          for (let page = 2; page <= lastPage; page++) {
            requests.push(this.productService.list({ page, per_page: perPage }));
          }
          return forkJoin(requests).pipe(
            map((pages) =>
              firstPage.concat(...pages.map((pageResult) => pageResult?.data?.products ?? [])),
            ),
          );
        }),
      )
      .subscribe({
        next: (products) => {
          this.items = products;
          this.paginationConfig.totalItems = this.items.length;
          this.clampPage();
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.toaster.error(error?.error?.message || 'Failed to load products.');
        },
      });
  }

  onPageChange(page: number): void {
    this.paginationConfig.currentPage = page;
  }

  statusLabel(status: number): string {
    return status === 1 ? 'Active' : status === 2 ? 'Inactive' : String(status ?? '-');
  }

  private clampPage(): void {
    const perPage = this.paginationConfig.itemsPerPage || 10;
    const maxPage = Math.max(1, Math.ceil((this.paginationConfig.totalItems ?? 0) / perPage));
    const current = this.paginationConfig.currentPage ?? 1;
    this.paginationConfig.currentPage = Math.min(Math.max(1, current), maxPage);
  }
}
